import React, { useState, useEffect } from 'react';
import { Palette, Check, RefreshCw, Plus, Edit2, Trash2, Eye, Save, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { themeAPI } from '../../services/api';

const ThemesTab = () => {
  const { currentTheme, activateTheme, loadThemes } = useTheme();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [customThemes, setCustomThemes] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    colors: {
      primary: '#e60023',
      secondary: '#f8f8f8',
      accent: '#0076d3',
      background: '#ffffff',
      cardBackground: '#ffffff',
      text: '#2c2c2c',
      textSecondary: '#767676',
      border: '#e5e7eb',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
    },
  });

  const predefinedThemes = [
    { name: 'default', displayName: 'Default', description: 'Classic red and white' },
    { name: 'spring', displayName: 'Spring', description: 'Fresh and vibrant' },
    { name: 'summer', displayName: 'Summer', description: 'Bright and sunny' },
    { name: 'autumn', displayName: 'Autumn', description: 'Warm and cozy' },
    { name: 'winter', displayName: 'Winter', description: 'Cool and crisp' },
  ];

  useEffect(() => {
    fetchCustomThemes();
  }, []);

  const fetchCustomThemes = async () => {
    try {
      const response = await themeAPI.getThemes();
      if (response.data.success) {
        // Filter out predefined themes
        const custom = response.data.data.filter(t => !t.isPredefined);
        setCustomThemes(custom);
      }
    } catch (err) {
      console.error('Failed to fetch custom themes:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleColorChange = (colorName, value) => {
    setFormData(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorName]: value,
      },
    }));
  };

  const handleCreateTheme = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await themeAPI.createTheme({
        ...formData,
        type: 'custom',
      });
      if (response.data.success) {
        alert('Theme created successfully!');
        setShowCreateForm(false);
        setFormData({
          name: '',
          displayName: '',
          description: '',
          colors: {
            primary: '#e60023',
            secondary: '#f8f8f8',
            accent: '#0076d3',
            background: '#ffffff',
            cardBackground: '#ffffff',
            text: '#2c2c2c',
            textSecondary: '#767676',
            border: '#e5e7eb',
            success: '#10b981',
            warning: '#f59e0b',
            error: '#ef4444',
          },
        });
        await fetchCustomThemes();
      }
    } catch (err) {
      alert('Error creating theme: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTheme = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await themeAPI.updateTheme(editingTheme._id, {
        displayName: formData.displayName,
        description: formData.description,
        colors: formData.colors,
      });
      if (response.data.success) {
        alert('Theme updated successfully!');
        setEditingTheme(null);
        await fetchCustomThemes();
        if (currentTheme?._id === editingTheme._id) {
          await loadThemes();
        }
      }
    } catch (err) {
      alert('Error updating theme: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTheme = async (themeId) => {
    if (!window.confirm('Are you sure you want to delete this theme?')) {
      return;
    }
    try {
      const response = await themeAPI.deleteTheme(themeId);
      if (response.data.success) {
        alert('Theme deleted successfully!');
        await fetchCustomThemes();
      }
    } catch (err) {
      alert('Error deleting theme: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditTheme = (theme) => {
    setEditingTheme(theme);
    setFormData({
      name: theme.name,
      displayName: theme.displayName,
      description: theme.description || '',
      colors: theme.colors,
    });
    setShowCreateForm(false);
  };

  const handleCancelEdit = () => {
    setEditingTheme(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      colors: {
        primary: '#e60023',
        secondary: '#f8f8f8',
        accent: '#0076d3',
        background: '#ffffff',
        cardBackground: '#ffffff',
        text: '#2c2c2c',
        textSecondary: '#767676',
        border: '#e5e7eb',
        success: '#10b981',
        warning: '#f59e0b',
        error: '#ef4444',
      },
    });
  };

  const handleActivateTheme = async (themeName) => {
    setLoading(true);
    try {
      const success = await activateTheme(themeName);
      if (success) {
        alert('Theme activated successfully!');
      } else {
        alert('Failed to activate theme');
      }
    } catch (err) {
      alert('Error activating theme: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCustomTheme = async (themeId) => {
    setLoading(true);
    try {
      const response = await themeAPI.activateTheme(themeId);
      if (response.data.success) {
        alert('Theme activated successfully!');
        await loadThemes();
      }
    } catch (err) {
      alert('Error activating theme: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePredefinedThemes = async () => {
    if (!window.confirm('This will update all predefined themes with the latest colors. Continue?')) {
      return;
    }

    setUpdating(true);
    try {
      const response = await themeAPI.updatePredefinedThemes();
      if (response.data.success) {
        alert(`Themes updated! ${response.data.data.updated.length} updated, ${response.data.data.created.length} created`);
        await loadThemes();
      }
    } catch (err) {
      alert('Error updating themes: ' + (err.response?.data?.error || err.message));
    } finally {
      setUpdating(false);
    }
  };

  const applyPreview = (colors) => {
    Object.entries(colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
  };

  const clearPreview = () => {
    if (currentTheme?.colors) {
      Object.entries(currentTheme.colors).forEach(([key, value]) => {
        document.documentElement.style.setProperty(`--${key}`, value);
      });
    }
    setPreviewTheme(null);
  };

  const ColorInput = ({ label, name, value, onChange }) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          style={{
            width: '60px',
            height: '40px',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(name, e.target.value)}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '14px',
          }}
        />
        <div
          style={{
            width: '40px',
            height: '40px',
            background: value,
            border: '1px solid var(--border)',
            borderRadius: '4px',
          }}
        />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ margin: 0 }}>Theme Management</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          {!showCreateForm && !editingTheme && (
            <button
              onClick={() => {
                setShowCreateForm(true);
                setEditingTheme(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              <Plus size={16} />
              Create Custom Theme
            </button>
          )}
          <button
            onClick={handleUpdatePredefinedThemes}
            disabled={updating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'var(--secondary)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              cursor: updating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: updating ? 0.6 : 1,
            }}
          >
            <RefreshCw size={16} style={{ animation: updating ? 'spin 1s linear infinite' : 'none' }} />
            {updating ? 'Updating...' : 'Update Predefined'}
          </button>
        </div>
      </div>

      {/* Create/Edit Theme Form */}
      {(showCreateForm || editingTheme) && (
        <div style={{
          background: 'var(--cardBackground)',
          border: '2px solid var(--primary)',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '32px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ margin: 0 }}>{editingTheme ? 'Edit Theme' : 'Create Custom Theme'}</h2>
            <button
              onClick={() => {
                setShowCreateForm(false);
                handleCancelEdit();
                clearPreview();
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--textSecondary)',
              }}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={editingTheme ? handleUpdateTheme : handleCreateTheme}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Left Column - Basic Info */}
              <div>
                <h3 style={{ marginTop: 0 }}>Basic Information</h3>

                {!editingTheme && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Theme ID *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., my-custom-theme"
                      required
                      disabled={!!editingTheme}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                    <small style={{ color: 'var(--textSecondary)', fontSize: '12px' }}>
                      Lowercase, no spaces (used in URL)
                    </small>
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Display Name *
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder="e.g., My Custom Theme"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of this theme"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                </div>

                {/* Preview Button */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      applyPreview(formData.colors);
                      setPreviewTheme(formData);
                    }}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px',
                      background: 'var(--accent)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    <Eye size={16} />
                    Preview Theme
                  </button>
                  {previewTheme && (
                    <button
                      type="button"
                      onClick={clearPreview}
                      style={{
                        padding: '10px 16px',
                        background: 'var(--secondary)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      Clear Preview
                    </button>
                  )}
                </div>
              </div>

              {/* Right Column - Colors */}
              <div>
                <h3 style={{ marginTop: 0 }}>Theme Colors</h3>
                <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '12px' }}>
                  <ColorInput
                    label="Primary Color"
                    name="primary"
                    value={formData.colors.primary}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Secondary Color"
                    name="secondary"
                    value={formData.colors.secondary}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Accent Color"
                    name="accent"
                    value={formData.colors.accent}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Background"
                    name="background"
                    value={formData.colors.background}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Card Background"
                    name="cardBackground"
                    value={formData.colors.cardBackground}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Text Color"
                    name="text"
                    value={formData.colors.text}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Secondary Text"
                    name="textSecondary"
                    value={formData.colors.textSecondary}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Border Color"
                    name="border"
                    value={formData.colors.border}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Success Color"
                    name="success"
                    value={formData.colors.success}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Warning Color"
                    name="warning"
                    value={formData.colors.warning}
                    onChange={handleColorChange}
                  />
                  <ColorInput
                    label="Error Color"
                    name="error"
                    value={formData.colors.error}
                    onChange={handleColorChange}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  handleCancelEdit();
                  clearPreview();
                }}
                style={{
                  padding: '10px 24px',
                  background: 'var(--secondary)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 24px',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : (editingTheme ? 'Update Theme' : 'Create Theme')}
              </button>
            </div>
          </form>
        </div>
      )}

      {previewTheme && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>
            🎨 Preview mode active - colors are temporarily applied
          </span>
          <button
            onClick={clearPreview}
            style={{
              padding: '6px 12px',
              background: 'white',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            Exit Preview
          </button>
        </div>
      )}

      {/* Current Theme */}
      <div style={{
        background: 'var(--cardBackground)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '32px',
      }}>
        <h2 style={{ marginTop: 0 }}>Current Active Theme</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Palette size={32} color="var(--primary)" />
          <div>
            <h3 style={{ margin: 0, marginBottom: '4px' }}>{currentTheme?.displayName || 'Default'}</h3>
            <p style={{ margin: 0, color: 'var(--textSecondary)' }}>
              Currently active theme for all users
            </p>
          </div>
        </div>

        {/* Color Preview */}
        <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '12px' }}>
          {currentTheme?.colors && Object.entries(currentTheme.colors).map(([key, value]) => (
            <div key={key} style={{ textAlign: 'center' }}>
              <div style={{
                width: '100%',
                height: '50px',
                background: value,
                borderRadius: '6px',
                border: '1px solid var(--border)',
                marginBottom: '8px',
              }} />
              <div style={{ fontSize: '11px', color: 'var(--textSecondary)', marginBottom: '2px' }}>{key}</div>
              <div style={{ fontSize: '9px', fontFamily: 'monospace', color: 'var(--textSecondary)' }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Themes */}
      {customThemes.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2>Custom Themes</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {customThemes.map((theme) => {
              const isActive = currentTheme?._id === theme._id;

              return (
                <div
                  key={theme._id}
                  style={{
                    background: 'var(--cardBackground)',
                    border: `2px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                    borderRadius: '8px',
                    padding: '20px',
                    position: 'relative',
                  }}
                >
                  {isActive && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'var(--success)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                      <Check size={14} />
                      Active
                    </div>
                  )}

                  <h3 style={{ marginTop: 0, marginBottom: '4px' }}>{theme.displayName}</h3>
                  <p style={{ margin: 0, marginBottom: '12px', fontSize: '13px', color: 'var(--textSecondary)' }}>
                    {theme.description || 'Custom theme'}
                  </p>

                  {/* Color Preview */}
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {Object.values(theme.colors).slice(0, 6).map((color, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: '24px',
                          height: '24px',
                          background: color,
                          borderRadius: '4px',
                          border: '1px solid var(--border)',
                        }}
                      />
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleActivateCustomTheme(theme._id)}
                      disabled={loading || isActive}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: isActive ? 'var(--secondary)' : 'var(--primary)',
                        color: isActive ? 'var(--textSecondary)' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (loading || isActive) ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                      }}
                    >
                      {isActive ? 'Active' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleEditTheme(theme)}
                      style={{
                        padding: '8px 12px',
                        background: 'var(--secondary)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteTheme(theme._id)}
                      disabled={isActive}
                      style={{
                        padding: '8px 12px',
                        background: 'var(--error)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isActive ? 'not-allowed' : 'pointer',
                        opacity: isActive ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Predefined Themes */}
      <div>
        <h2>Predefined Themes</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
          {predefinedThemes.map((theme) => {
            const isActive = currentTheme?.name === theme.name;

            return (
              <div
                key={theme.name}
                style={{
                  background: 'var(--cardBackground)',
                  border: `2px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: '8px',
                  padding: '20px',
                  position: 'relative',
                }}
              >
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'var(--success)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <Check size={14} />
                    Active
                  </div>
                )}

                <h3 style={{ marginTop: 0, marginBottom: '8px' }}>{theme.displayName}</h3>
                <p style={{ margin: 0, marginBottom: '16px', fontSize: '14px', color: 'var(--textSecondary)' }}>
                  {theme.description}
                </p>

                <button
                  onClick={() => handleActivateTheme(theme.name)}
                  disabled={loading || isActive}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: isActive ? 'var(--secondary)' : 'var(--primary)',
                    color: isActive ? 'var(--textSecondary)' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (loading || isActive) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {isActive ? 'Currently Active' : 'Activate Theme'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ThemesTab;
